import React, { useState, useEffect, useMemo, useRef } from 'react'
import QRCode from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@mui/material'
import {
  Box,
  Typography,
  TextField,
  useTheme,
  Link,
  Stack,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAuth } from 'auth'
import logger from 'logger'
import config from 'config'

import InputLabel from 'components/atoms/InputLabel'

const wait = (time) => new Promise((resolve) => setTimeout(resolve, time))

const MfaSetup = ({
  setView,
  state,
  setError,
  error,
  setState,
  setFetching,
  fetching,
  setLoginError,
}) => {
  const theme = useTheme()
  const { AmplifyAuth, authEvent, signOut } = useAuth()
  const { t } = useTranslation('auth')
  const inputEl = useRef(null)

  useEffect(() => {
    setTimeout(() => {
      if (inputEl?.current) {
        inputEl.current.focus()
      }
    }, 50)
  }, [inputEl])

  const [setupCode, setSetupCode] = useState()

  const handleInputChange = (event) => {
    setLoginError()
    setState((s) => ({ ...s, [event.target.name]: event.target.value }))
  }

  useEffect(() => {
    async function run() {
      const response = await AmplifyAuth.setupTOTP(authEvent)
      setSetupCode(response)
    }

    run()
  }, [AmplifyAuth, authEvent])

  const handleVerifyOtp = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    try {
      setFetching(true)
      await AmplifyAuth.verifyTotpToken(authEvent, state.otpCode1)

      // It's required to wait and re-signin to be sure that the MFA is set properly
      await wait(1000)
      await AmplifyAuth.setPreferredMFA(authEvent, 'SOFTWARE_TOKEN_MFA')
      await wait(1000)
      await AmplifyAuth.setPreferredMFA(authEvent, 'SOFTWARE_TOKEN_MFA')
      await wait(1000)

      setFetching(false)
      setState((s) => ({ ...s, otpCode1: '', otpCode2: '', otpCode: '' }))

      signOut()

      setView('signIn')
    } catch (e) {
      logger.error(e)
      setError(e.code)
    } finally {
      setFetching(false)
    }
  }

  const otpUri = useMemo(
    () =>
      `otpauth://totp/Canalizzazione%20Arval${config.auth.mfaPrefix}:${state.email}?secret=${setupCode}&issuer=Canalizzazione%20Arval&algorithm=SHA1&digits=6&period=30`,
    [setupCode, state.email]
  )

  return (
    <Stack
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        width: 0.5,
        maxWidth: 400,
      }}
    >
      <form onSubmit={handleVerifyOtp} style={{ width: '100%' }}>
        <Typography variant="h4" sx={{ pb: 1 }}>
          <strong>{t('headers.mfaSetup.title')}</strong>
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ pb: 2 }}>
          <strong>{t('headers.mfaSetup.subtitle')}</strong>
        </Typography>

        <Box mb={2} height="210px">
          {setupCode ? (
            <QRCode value={otpUri} size={200} data-testid="mfaSetup-qrcode" />
          ) : (
            <Box height="200px">
              <CircularProgress />
            </Box>
          )}
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ pb: 2, fontSize: '85%' }}
        >
          <strong>{t('headers.mfaSetup.info')}</strong>
        </Typography>

        <Box sx={{ width: 1 }}>
          <InputLabel
            label={t('attributes.otpCode1.label')}
            inputId="mfa-setup-code1"
            sx={{ textAlign: 'left' }}
          />
          <TextField
            id="mfa-setup-code1"
            size="small"
            fullWidth
            placeholder={t('attributes.otpCode1.placeholder')}
            name="otpCode1"
            value={state.otpCode1}
            onChange={handleInputChange}
            disabled={fetching}
            inputProps={{
              ref: inputEl,
            }}
            InputProps={{
              'data-testid': 'mfaSetup-otpCode1',
            }}
          />
        </Box>

        <Box mb={2} />
        <LoadingButton
          fullWidth
          width="240px"
          variant="contained"
          color="primary"
          loading={fetching}
          disabled={fetching}
          type="submit"
          data-testid="mfaSetup-button"
        >
          {t('buttons.verifySetupMfa')}
        </LoadingButton>
        {error && (
          <Typography
            textAlign="center"
            p={[3]}
            style={{ color: theme.palette.error.light }}
            data-testid="mfaSetup-error"
          >
            {t(`errors.${error}`, state)}
          </Typography>
        )}

        <Box mb={2} />
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ pt: 2, fontSize: '85%' }}
        >
          <strong>{t('headers.mfaSetup.installApp')}</strong>
        </Typography>
        <Link
          href="https://apps.apple.com/app/google-authenticator/id388497605"
          target="_blank"
        >
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer' }}
          >
            <strong>{t('buttons.installAppLink')}</strong>
          </Typography>
        </Link>
        <Box mb={3} />
        <Link>
          <Typography
            variant="body2"
            color="primary"
            data-testid="mfaSetup-backToSignin"
            onClick={() => {
              setState((s) => ({ ...s, password: '', email: '' }))
              setView('signIn')
            }}
            sx={{ cursor: 'pointer' }}
          >
            <strong>{t('buttons.loginWithAnotherUser')}</strong>
          </Typography>
        </Link>
      </form>
    </Stack>
  )
}

export default MfaSetup
